import {
  Button,
  ButtonGroup,
  Flex,
  Heading,
  Input,
  Text,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import Resizer from "react-image-file-resizer";
import { useNavigate } from "react-router-dom";
import { CardRecord } from "components/cards/card-record";
import { Form } from "components/form";
import { FormField } from "components/form-field";
import { MultiUploadImage } from "components/inputs/multi-image-upload-input";
import { Page } from "components/page";
import { usePropertyTenancy } from "contexts/property-tenancy-provider";
import {
  tenancyMaintenanceModel,
  type CreateMaintenanceTaskPayload,
  type MaintenanceFormValues,
} from "modules/tenant-portal/models/maintenance";

export function RequestMaintenanceScreen() {
  const navigate = useNavigate();
  const toast = useToast();
  const MAX_SIZE = 2 * 1024 * 1024; // 2MB
  const MIN_QUALITY = 50;
  const { propertyTenancyId } = usePropertyTenancy();
  const form = useForm({
    defaultValues: {
      summary: "",
      description: "",
      images: [],
    },
  });

  const createMaintenanceMutation = useMutation({
    ...tenancyMaintenanceModel.mutations.create(),
    onSuccess: () => {
      toast({
        title: "Maintenance Task Created",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      navigate(-1);
    },
    onError: () => {
      form.reset();
    },
  });

  function resizeWithQuality(file: File, quality: number): Promise<File> {
    return new Promise((resolve) => {
      Resizer.imageFileResizer(
        file,
        1500,
        1500,
        "JPEG",
        quality,
        0,
        (uri) => resolve(uri as File),
        "file",
      );
    });
  }

  async function compressImageUnder2MB(file: File): Promise<File> {
    let quality = 90;
    let compressed = await resizeWithQuality(file, quality);

    while (compressed.size > MAX_SIZE && quality > MIN_QUALITY) {
      quality -= 10;
      compressed = await resizeWithQuality(file, quality);
    }

    return compressed;
  }

  const handleSubmit = async (values: MaintenanceFormValues) => {
    const processedImages = await Promise.all(
      values.images.map((file) => compressImageUnder2MB(file)),
    );

    const payload: CreateMaintenanceTaskPayload = {
      summary: values.summary,
      description: values.description,
      property_tenancy_id: propertyTenancyId,
      images: processedImages,
    };

    await createMaintenanceMutation.mutateAsync(payload);
  };

  return (
    <Page heading={"Request Maintenance"} showBackButton>
      <Form id={"request-maintenance"} form={form} onSubmit={handleSubmit}>
        <Flex direction={"column"} gap={6}>
          <CardRecord py={8}>
            <Flex direction={["column", "column", "row"]} gap={6}>
              <Flex direction={"column"} gap={2} flex={1}>
                <Heading size={"md"} fontWeight={"bold"}>
                  Details
                </Heading>
                <Text color={"gray.600"}>
                  Tell us as much information as you can about the problem so
                  that we know how to help
                </Text>
              </Flex>
              <Flex flex={2} direction={"column"} gap={4}>
                <FormField
                  name={"summary"}
                  label={"Summary"}
                  Input={Input}
                  required
                  inputProps={{
                    placeholder:
                      "Enter a brief summary of the maintenance required",
                  }}
                />
                <FormField
                  name={"description"}
                  label={"Detailed description"}
                  Input={Textarea}
                  inputProps={{
                    placeholder:
                      "Tell us more about the issue so that we can get the right person to help",
                  }}
                />
              </Flex>
            </Flex>
          </CardRecord>
          <CardRecord py={8}>
            <Flex direction={["column", "column", "row"]} gap={6}>
              <Flex direction={"column"} gap={2} flex={1}>
                <Heading size={"md"} fontWeight={"bold"}>
                  Attachments
                </Heading>
                <Text color={"gray.600"}>
                  By adding photos or videos, you can help us understand the
                  problem better and we can get to a solution quicker
                </Text>
              </Flex>
              <Flex flex={2}>
                <FormField
                  name={"images"}
                  label={"Upload Attachments"}
                  Input={MultiUploadImage}
                />
              </Flex>
            </Flex>
          </CardRecord>
          <ButtonGroup justifyContent={"flex-end"}>
            <Button
              variant={"outline"}
              colorScheme={"gray"}
              onClick={() => {
                form.reset();
                window.scroll(0, 0);
              }}
            >
              Cancel
            </Button>
            <Button
              type={"submit"}
              isLoading={form.formState.isSubmitting}
              colorScheme={"gray"}
              variant={"solid"}
            >
              Submit request
            </Button>
          </ButtonGroup>
        </Flex>
      </Form>
    </Page>
  );
}
